/**
 * Parses the assistant's answers into a small, typed node tree.
 *
 * The assistant writes markdown by habit — bold for the key fact, "- " bullets
 * when it lists records — and the widget used to print it literally, so
 * employees read "**3 ataskaitos**".
 *
 * Two properties shape everything here:
 *
 * 1. This text is MODEL output derived from database rows, so a customer name
 *    or a file note reaches this parser unchanged. It is untrusted input.
 * 2. It arrives token by token, so every intermediate prefix of a sentence is
 *    parsed and shown before the final one exists.
 *
 * Consequently this file produces DATA, never HTML — the renderer turns nodes
 * into React elements, so there is no HTML string, no sanitiser, and no parser
 * disagreement for mutation-XSS to live in. A deliberately small subset is
 * supported; anything else stays the literal characters the model wrote, which
 * is honest and cannot introduce a sink.
 */

export type LinkTarget =
  | { kind: "internal"; path: string }
  | { kind: "external"; href: string };

export type InlineNode =
  | { type: "text"; value: string }
  | { type: "break" }
  | { type: "strong"; children: InlineNode[] }
  | { type: "em"; children: InlineNode[] }
  | { type: "code"; value: string }
  | { type: "link"; target: LinkTarget; label: string };

export interface ListItem {
  children: InlineNode[];
  nested?: { ordered: boolean; items: ListItem[]; start: number };
}

export type BlockNode =
  | { type: "paragraph"; children: InlineNode[] }
  | { type: "heading"; children: InlineNode[] }
  | { type: "list"; ordered: boolean; items: ListItem[]; start: number };

/**
 * Past this, the inlined answer is pathological rather than long — the backend
 * caps generation at 4096 tokens — so it degrades to plain text instead of
 * being parsed.
 */
const MAX_CONTENT_CHARS = 32768;

/** Emphasis may nest one level deep, so `**[Sutartis](…)**` works. */
const MAX_INLINE_DEPTH = 2;

/**
 * Bidi overrides and zero-width characters, stripped from the whole content
 * before anything looks at it. Without this a database value could reverse the
 * text an employee reads, or hide characters inside a URL. Removing characters
 * can only fail closed: `java<ZWSP>script:` collapses to `javascript:`, which
 * the scheme allow-list then rejects.
 */
const INVISIBLE =
  /[\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]/g;

/**
 * Whitespace and control characters as the URL-facing spec treats them.
 * `String.trim()` is not a substitute: it removes neither NUL nor a tab sitting
 * inside `java\tscript:`.
 */
const URL_BLANK =
  /[\u0000-\u0020\u007F\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]/g;

/** Allow-list, never a deny-list: an unknown future scheme fails closed. */
const ALLOWED_SCHEMES = ["http:", "https:"];

/**
 * The single gate between model text and a link.
 *
 * Both the `[label](destination)` branch and the bare-URL branch go through
 * here, and nothing else in the feature may construct a link.
 */
export function resolveLinkTarget(
  raw: string,
  origin: string
): LinkTarget | null {
  const probe = raw.replace(URL_BLANK, "").toLowerCase();
  if (!probe) return null;

  // Protocol-relative and backslash authority forms, rejected before any
  // scheme test. `new URL("\\\\evil.lt/x", origin).origin` is "https://evil.lt",
  // so checking forward slashes alone is not enough.
  if (/^[/\\]{2}/.test(probe)) return null;

  const colon = probe.indexOf(":");
  const slash = probe.search(/[/\\?#]/);
  const hasScheme = colon !== -1 && (slash === -1 || colon < slash);
  if (hasScheme && ALLOWED_SCHEMES.indexOf(probe.slice(0, colon + 1)) === -1) {
    return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(raw, origin);
  } catch {
    return null;
  }
  // `new URL("javascript:alert(1)")` parses happily with origin "null", so an
  // origin comparison alone would call it external and render a live href.
  if (ALLOWED_SCHEMES.indexOf(parsed.protocol) === -1) return null;

  if (parsed.origin !== origin) {
    return { kind: "external", href: parsed.href };
  }

  const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  // A same-origin URL can still carry a "//host" pathname, which react-router
  // would forward verbatim into a protocol-relative href.
  if (!/^\/(?!\/)/.test(path)) return null;
  return { kind: "internal", path };
}

/** Trailing punctuation belongs to the sentence, not the address. */
export function trimTrailingPunctuation(url: string): {
  href: string;
  tail: string;
} {
  const match = url.match(/[.,;:!?]+$/);
  if (!match) return { href: url, tail: "" };
  return { href: url.slice(0, -match[0].length), tail: match[0] };
}

/**
 * One alternation over every inline construct. Cloned on every call so
 * `lastIndex` can never leak from one message into the next.
 */
const TOKEN_SOURCE = [
  "\\*\\*(?![\\s*])([^\\n*]*?[^\\s*])\\*\\*",
  "\\*(?![\\s*])([^\\n*]*?[^\\s*])\\*",
  "`([^`\\n]+)`",
  "(!?)\\[([^\\]\\n]*)\\]\\(([^()\\s]*)\\)",
  "(https?://[^\\s<>\"')\\]]+)",
].join("|");

const GROUP = { bold: 1, em: 2, code: 3, bang: 4, label: 5, dest: 6, url: 7 };

interface InlineOptions {
  origin: string;
  /** Apply the streaming guards to this line's tail. */
  guardTail: boolean;
  depth: number;
}

function pushText(nodes: InlineNode[], value: string): void {
  if (!value) return;
  const last = nodes[nodes.length - 1];
  if (last && last.type === "text") {
    last.value += value;
    return;
  }
  nodes.push({ type: "text", value });
}

/**
 * Half-typed markup at the very end of the streaming message.
 *
 * Without this the employee watches `**`, a backtick or a `[` appear as
 * literal characters and then vanish a frame later. Only ever applied to the
 * tail of the last line, and never once the message is complete.
 */
function guardStreamingTail(
  remainder: string,
  options: InlineOptions
): InlineNode[] {
  const nodes: InlineNode[] = [];

  // A half-written link: show the label, never hand a partial destination to
  // the gate. The "](" is required — a bare "[" may just be a bracket in the
  // text, and hiding it until its closing bracket arrives would be the very
  // flicker this guard exists to prevent.
  const link = remainder.match(/(!?)\[([^\][\n]*)\]\([^()\s]*$/);
  if (link) {
    pushText(nodes, remainder.slice(0, link.index));
    pushText(nodes, link[2]);
    return nodes;
  }

  const code = remainder.match(/`([^`\n]*)$/);
  if (code) {
    pushText(nodes, remainder.slice(0, code.index));
    pushText(nodes, code[1]);
    return nodes;
  }

  // Bold that has opened but not closed: render it bold already, so the text
  // does not visibly re-flow when the closing asterisks arrive.
  const bold = remainder.match(/\*\*([^*]*)$/);
  if (bold) {
    pushText(nodes, remainder.slice(0, bold.index));
    nodes.push({
      type: "strong",
      // Still the tail, so the guards must apply inside it too: the content of
      // an unclosed bold is exactly where the next delta lands, and a URL in
      // there is still growing.
      children: parseInline(bold[1], {
        ...options,
        guardTail: true,
        depth: options.depth + 1,
      }),
    });
    return nodes;
  }

  pushText(nodes, remainder);
  return nodes;
}

/**
 * The position of a delimiter that has opened and not closed.
 *
 * Counting matters: a line ending in a COMPLETED `**bold**` also ends in a
 * `**`, and treating that closer as an opener would tear the finished bold
 * apart on every frame until the stream ended.
 */
function lastUnpairedIndex(line: string, token: string): number {
  let count = 0;
  let last = -1;
  let at = line.indexOf(token);
  while (at !== -1) {
    count += 1;
    last = at;
    at = line.indexOf(token, at + token.length);
  }
  return count % 2 === 1 ? last : -1;
}

/**
 * Where a construct opens at the tail and has not closed yet.
 *
 * Found before tokenising, not after: the bare-URL alternative would otherwise
 * match the destination inside an unclosed "](" and render a live link to a
 * half-arrived address.
 */
function unfinishedTailIndex(line: string): number {
  const link = line.match(/(!?)\[[^\][\n]*\]\([^()\s]*$/);
  const starts = [
    link && link.index !== undefined ? link.index : -1,
    lastUnpairedIndex(line, "`"),
    lastUnpairedIndex(line, "**"),
  ].filter((index) => index >= 0);
  return starts.length ? Math.min(...starts) : -1;
}

function parseInline(line: string, options: InlineOptions): InlineNode[] {
  const nodes: InlineNode[] = [];
  if (!line) return nodes;
  if (options.depth >= MAX_INLINE_DEPTH) {
    pushText(nodes, line);
    return nodes;
  }

  if (options.guardTail) {
    const cut = unfinishedTailIndex(line);
    if (cut >= 0) {
      const settled = parseInline(line.slice(0, cut), {
        ...options,
        guardTail: false,
      });
      guardStreamingTail(line.slice(cut), options).forEach((node) => {
        if (node.type === "text") pushText(settled, node.value);
        else settled.push(node);
      });
      return settled;
    }
  }

  const token = new RegExp(TOKEN_SOURCE, "g");
  let cursor = 0;
  let match = token.exec(line);

  while (match) {
    pushText(nodes, line.slice(cursor, match.index));
    const end = match.index + match[0].length;
    const child = { ...options, depth: options.depth + 1, guardTail: false };

    if (match[GROUP.bold] !== undefined) {
      nodes.push({ type: "strong", children: parseInline(match[GROUP.bold], child) });
    } else if (match[GROUP.em] !== undefined) {
      nodes.push({ type: "em", children: parseInline(match[GROUP.em], child) });
    } else if (match[GROUP.code] !== undefined) {
      nodes.push({ type: "code", value: match[GROUP.code] });
    } else if (match[GROUP.dest] !== undefined) {
      // `bang` participates as "" whenever the link branch fires, so this must
      // compare rather than test truthiness.
      const isImage = match[GROUP.bang] === "!";
      const target = isImage
        ? null
        : resolveLinkTarget(match[GROUP.dest], options.origin);
      if (target) {
        nodes.push({ type: "link", target, label: match[GROUP.label] });
      } else {
        // Inert and visible: the whole match becomes one text node, and is
        // never re-scanned, so a URL inside an image or a rejected link cannot
        // be picked up by the bare-URL branch either.
        pushText(nodes, match[0]);
      }
    } else if (match[GROUP.url] !== undefined) {
      const { href, tail } = trimTrailingPunctuation(match[GROUP.url]);
      // Not "&& !tail": a URL whose current last character is a period is
      // still growing, and trimming that period must not make it look settled.
      const atVeryEnd = end === line.length;
      const target = resolveLinkTarget(href, options.origin);
      if (target && !(options.guardTail && atVeryEnd)) {
        nodes.push({ type: "link", target, label: href });
      } else {
        // Still growing: "/orders/1" may become "/orders/12", and a link that
        // briefly points at the wrong record is worse than one that waits.
        pushText(nodes, href);
      }
      pushText(nodes, tail);
    }

    cursor = end;
    match = token.exec(line);
  }

  const remainder = line.slice(cursor);
  if (options.guardTail) {
    for (const node of guardStreamingTail(remainder, options)) {
      if (node.type === "text") pushText(nodes, node.value);
      else nodes.push(node);
    }
  } else {
    pushText(nodes, remainder);
  }

  return nodes;
}

const HEADING = /^ {0,3}#{1,6}[ \t]+(.*)$/;
const BULLET = /^( {0,7})[-*+][ \t]+(.*)$/;
const ORDERED = /^( {0,7})(\d{1,2})[.)][ \t]+(.*)$/;
const BLANK = /^[ \t]*$/;

interface PendingList {
  ordered: boolean;
  items: ListItem[];
  /** Indent the list opened at — nesting is relative to this, not to zero. */
  indent: number;
  /** The number the model wrote for the first item, so <ol> can start there. */
  start: number;
}

/**
 * Turn the assistant's answer into blocks and inline nodes.
 *
 * Lines are classified independently — no lazy continuation, no indented code,
 * no block state machine. That is what keeps a hand-written parser honest, and
 * it also means a half-arrived last line is at worst a paragraph that
 * reclassifies once its newline lands.
 */
export function parseAssistantMarkdown(
  content: string,
  origin: string,
  options: { streaming?: boolean } = {}
): BlockNode[] {
  const normalized = String(content).replace(/\r\n?/g, "\n").replace(INVISIBLE, "");
  if (!normalized) return [];

  if (normalized.length > MAX_CONTENT_CHARS) {
    return [{ type: "paragraph", children: [{ type: "text", value: normalized }] }];
  }

  const lines = normalized.split("\n");
  const lastIndex = lines.length - 1;
  const blocks: BlockNode[] = [];

  let paragraph: { text: string; index: number }[] | null = null;
  let list: PendingList | null = null;

  const inlineFor = (text: string, index: number): InlineNode[] =>
    parseInline(text, {
      origin,
      guardTail: !!options.streaming && index === lastIndex,
      depth: 0,
    });

  const closeParagraph = () => {
    if (!paragraph) return;
    const children: InlineNode[] = [];
    paragraph.forEach((entry, position) => {
      if (position > 0) children.push({ type: "break" });
      children.push(...inlineFor(entry.text, entry.index));
    });
    blocks.push({ type: "paragraph", children });
    paragraph = null;
  };

  const closeList = () => {
    if (!list) return;
    blocks.push({
      type: "list",
      ordered: list.ordered,
      items: list.items,
      start: list.start,
    });
    list = null;
  };

  lines.forEach((line, index) => {
    if (BLANK.test(line)) {
      closeParagraph();
      closeList();
      return;
    }

    const heading = line.match(HEADING);
    if (heading) {
      closeParagraph();
      closeList();
      blocks.push({ type: "heading", children: inlineFor(heading[1], index) });
      return;
    }

    const ordered = line.match(ORDERED);
    const bullet = ordered ? null : line.match(BULLET);
    if (ordered || bullet) {
      closeParagraph();
      const isOrdered = !!ordered;
      const indent = (ordered ? ordered[1] : (bullet as RegExpMatchArray)[1]).length;
      const text = ordered ? ordered[3] : (bullet as RegExpMatchArray)[2];
      // The model's own numbering: a blank line between steps closes the list,
      // so without this every "2." and "3." would render as "1." again.
      const number = ordered ? Number(ordered[2]) : 1;
      // Nesting is relative to the indent the list OPENED at, not to zero:
      // a whole list indented under a lead-in sentence is still one flat list,
      // and treating every line as a child would fold all its siblings into
      // the first item. A second level is the last one a 261px column can
      // resolve, so depth is capped by only ever nesting under a top-level
      // item.
      // This is decided BEFORE the ordered/unordered comparison, because
      // "1. įrašas" with "  - detalė" under it is a child; comparing flags
      // first would close the numbered list and start a second one instead.
      const deeper = !!list && indent >= list.indent + 2;
      const parent =
        list && deeper ? list.items[list.items.length - 1] : undefined;
      // A nested list holds one type. If a child of the other type appears,
      // demote it to a sibling rather than draw a bullet with a number.
      const typeClash = !!parent && !!parent.nested && parent.nested.ordered !== isOrdered;
      const nestHere = parent && !typeClash ? parent : undefined;

      if (list && !nestHere && list.ordered !== isOrdered) closeList();
      if (!list) list = { ordered: isOrdered, items: [], indent, start: number };

      const item: ListItem = { children: inlineFor(text, index) };
      if (nestHere) {
        if (!nestHere.nested) {
          nestHere.nested = { ordered: isOrdered, items: [], start: number };
        }
        nestHere.nested.items.push(item);
      } else {
        list.items.push(item);
      }
      return;
    }

    closeList();
    if (!paragraph) paragraph = [];
    paragraph.push({ text: line, index });
  });

  closeParagraph();
  closeList();
  return blocks;
}
